/**
 * CSRF Form Protection
 *
 * Refreshes CSRF tokens before standard HTML form submissions to prevent
 * "Unable to verify your data submission" errors caused by stale tokens
 * (page caching, browser bfcache, session expiration, etc.).
 *
 * Since pages are edge-cached, `csrfInput()` renders as an empty
 * `<craft-csrf-input>` placeholder (see `asyncCsrfInputs` in config/general.php)
 * that Craft fills in asynchronously. This handler is what guarantees a form
 * always carries a valid token at submit time, whether or not that async fill
 * has completed.
 *
 * Handles all POST forms. Excludes Formie forms, which have their own CSRF
 * refresh mechanism.
 */

import { getCsrfToken, setCsrfToken } from './csrf-token.js';

const CSRF_INPUT_NAME = 'CRAFT_CSRF_TOKEN';

/**
 * Check if a form is managed by Formie (which has its own CSRF handling)
 */
function isFormieForm(form) {
    return form.classList.contains('fui-form') ||
        form.hasAttribute('data-fui-form') ||
        !!form.closest('.fui-i');
}

/**
 * Find the form's CSRF input, creating one if Craft's async placeholder
 * hasn't been replaced yet (or was never populated).
 *
 * @param {HTMLFormElement} form
 * @returns {HTMLInputElement}
 */
function ensureCsrfInput(form) {
    const existing = form.querySelector(`input[name="${CSRF_INPUT_NAME}"]`);
    if (existing) return existing;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = CSRF_INPUT_NAME;

    // Craft's async placeholder is where the token is meant to live; put the
    // input there when present so field ordering stays intact.
    const placeholder = form.querySelector('craft-csrf-input');
    if (placeholder) {
        placeholder.appendChild(input);
    } else {
        form.appendChild(input);
    }

    return input;
}

/**
 * Initialize global CSRF protection on standard POST forms.
 * Intercepts form submission, fetches a fresh token, then submits.
 */
function initCsrfFormProtection() {
    document.addEventListener('submit', async function (e) {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        // Another handler has taken over this submission (e.g. the addresses
        // form in pages/checkout.js posts via fetch) and resolves its own token.
        // Submitting natively here as well would send a second, unintended POST.
        if (e.defaultPrevented) return;
        if (form.method.toLowerCase() !== 'post') return;
        if (isFormieForm(form)) return;

        // Only manage forms that post back to Craft.
        const isCraftForm = form.querySelector(`input[name="${CSRF_INPUT_NAME}"]`) ||
            form.querySelector('craft-csrf-input') ||
            form.querySelector('input[name="action"]');
        if (!isCraftForm) return;

        e.preventDefault();

        const submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        // Always force a refresh: a cached page may have been restored from
        // bfcache hours after the token it carries was issued.
        const freshToken = await getCsrfToken({ force: true });

        if (freshToken) {
            ensureCsrfInput(form).value = freshToken;
            setCsrfToken(freshToken);
        }
        // If the refresh failed, submit anyway: any token already in the form
        // may still be valid, and Craft surfaces a clear error if it isn't.

        // form.submit() sends the form directly without re-firing the submit event
        form.submit();
    }, true);
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCsrfFormProtection);
} else {
    initCsrfFormProtection();
}

export { initCsrfFormProtection };
